import { prefillShopUrl } from '../ImportSources/prefillSourceUrl';
import type { PlatformVerdict } from './proposalCachePolicy';

/**
 * "Verify platform" for a brand proposal: does the brand's site actually run
 * Shopify or WooCommerce, whatever the proposal claims?
 *
 *  - Shopify:     GET {origin}/products.json?limit=1 → a JSON object with a
 *                 `products` array.
 *  - WooCommerce: GET {origin}/wp-json/wc/store/v1/products?per_page=1 → a
 *                 JSON array.
 *
 * GET, not HEAD: both run server-side (the browser cannot read these
 * cross-origin), through `fetchHtml`, which only does GET — and some hosts
 * answer HEAD differently from GET anyway. A one-item page is tiny. The body
 * is checked, not just the status: plenty of sites answer 200 with an HTML
 * page for any path.
 *
 * A probe that was refused or could not connect proves nothing, so the
 * verdict is "neither" only when BOTH probes got a definite answer that is
 * not their platform; otherwise "unreachable" ("could not reach site").
 */

type ProbeOutcome = 'yes' | 'no' | 'unreachable';

export interface PlatformProbeDeps {
  /** Server-side GET returning the body; throws (with `status` when known) on failure. */
  fetchShopify: (url: string) => Promise<string>;
  /** Same for the WooCommerce Store API (the escalation ladder). */
  fetchWoo: (url: string) => Promise<string>;
}

export function siteRoot(websiteUrl: string): string | null {
  const url = prefillShopUrl(websiteUrl);
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function parse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** A failure is "no" only when the site answered: a 404/410 or other 4xx that is not a block. */
function outcomeOfError(err: unknown): ProbeOutcome {
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status !== 'number' || status === 0 || status >= 500) return 'unreachable';
  if ((err as { blockedByHost?: unknown }).blockedByHost === true) return 'unreachable';
  if ([401, 403, 406, 429].includes(status)) return 'unreachable';
  return 'no';
}

async function probe(
  fetchText: (url: string) => Promise<string>,
  url: string,
  isMatch: (body: unknown) => boolean,
): Promise<ProbeOutcome> {
  try {
    return isMatch(parse(await fetchText(url))) ? 'yes' : 'no';
  } catch (err) {
    return outcomeOfError(err);
  }
}

export async function verifyStorePlatform(
  websiteUrl: string,
  deps: PlatformProbeDeps,
): Promise<{ verdict: PlatformVerdict; siteUrl: string }> {
  const root = siteRoot(websiteUrl);
  if (!root) return { verdict: 'unreachable', siteUrl: websiteUrl };
  const [shopify, woo] = await Promise.all([
    probe(deps.fetchShopify, `${root}/products.json?limit=1`, (b) =>
      Array.isArray((b as { products?: unknown } | null)?.products),
    ),
    probe(deps.fetchWoo, `${root}/wp-json/wc/store/v1/products?per_page=1`, (b) => Array.isArray(b)),
  ]);
  const verdict: PlatformVerdict =
    shopify === 'yes'
      ? 'shopify'
      : woo === 'yes'
        ? 'woocommerce'
        : shopify === 'no' && woo === 'no'
          ? 'neither'
          : 'unreachable';
  return { verdict, siteUrl: root };
}

export const PLATFORM_VERDICT_LABELS: Record<PlatformVerdict, string> = {
  shopify: 'Confirmed Shopify',
  woocommerce: 'Confirmed WooCommerce',
  neither: 'Neither detected',
  unreachable: 'Could not reach site',
};

/**
 * What the proposal claims, normalised to the probe's vocabulary, or null when
 * it claims neither. MA has both a normalised `ecommerce_platform` and the
 * older `is_shopify` flag.
 */
export function claimedPlatform(detail: {
  ecommerce_platform?: string | null;
  is_shopify?: boolean | null;
}): 'shopify' | 'woocommerce' | null {
  const platform = (detail.ecommerce_platform ?? '').toLowerCase();
  if (platform.includes('shopify') || detail.is_shopify === true) return 'shopify';
  if (platform.includes('woo')) return 'woocommerce';
  return null;
}

/** True when the proposal claims Shopify/WooCommerce and the probe found something else definite. */
export function isPlatformMismatch(
  claimed: 'shopify' | 'woocommerce' | null,
  verdict: PlatformVerdict,
): boolean {
  if (!claimed || verdict === 'unreachable') return false;
  return verdict !== claimed;
}
